import DOMPurify from "dompurify";
import ReactMarkdown from "react-markdown";
import rehypeSanitize from "rehype-sanitize";
import { isProbablyRichTextHtml, isStipInstructionsEmpty } from "@/utils/stipInstructions";

const RT_ALLOWED_TAGS = [
  "p",
  "br",
  "strong",
  "b",
  "em",
  "i",
  "u",
  "s",
  "ul",
  "ol",
  "li",
  "a",
  "h1",
  "h2",
  "h3",
  "blockquote",
  "code",
  "pre",
  "hr",
];

const RT_ALLOWED_ATTR = ["href", "title", "rel", "target", "cite"];

type Props = {
  /** Stored stip instructions: rich HTML (TipTap) or legacy markdown/plain text. */
  content: string;
  className?: string;
};

/**
 * Renders stip instructions: sanitized HTML when content looks like HTML, otherwise markdown.
 */
export function StipInstructionsContent({ content, className = "" }: Props) {
  const raw = content || "";
  if (!raw.trim()) return null;
  if (isProbablyRichTextHtml(raw) && isStipInstructionsEmpty(raw)) return null;

  if (isProbablyRichTextHtml(raw)) {
    const safe = DOMPurify.sanitize(raw, {
      ALLOWED_TAGS: RT_ALLOWED_TAGS,
      ALLOWED_ATTR: RT_ALLOWED_ATTR,
      ALLOW_DATA_ATTR: false,
    });
    return (
      <div
        className={`text-sm text-gray-800 [&_a]:text-blue-600 [&_a]:underline [&_blockquote]:border-l-2 [&_blockquote]:border-gray-300 [&_blockquote]:pl-3 [&_code]:rounded [&_code]:bg-gray-100 [&_code]:px-1 [&_code]:text-xs [&_h1]:text-base [&_h1]:font-semibold [&_h2]:text-sm [&_h2]:font-semibold [&_h3]:text-sm [&_h3]:font-medium [&_li]:my-0.5 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:my-1 [&_pre]:overflow-x-auto [&_pre]:rounded [&_pre]:bg-gray-100 [&_pre]:p-2 [&_pre]:text-xs [&_ul]:list-disc [&_ul]:pl-5 ${className}`}
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: safe }}
      />
    );
  }

  return (
    <div
      className={`text-sm text-gray-800 [&_a]:text-blue-600 [&_a]:underline [&_blockquote]:border-l-2 [&_blockquote]:border-gray-300 [&_blockquote]:pl-3 [&_code]:rounded [&_code]:bg-gray-100 [&_code]:px-1 [&_code]:text-xs [&_h1]:text-base [&_h1]:font-semibold [&_h2]:text-sm [&_h2]:font-semibold [&_h3]:text-sm [&_h3]:font-medium [&_li]:my-0.5 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:my-1 [&_pre]:overflow-x-auto [&_pre]:rounded [&_pre]:bg-gray-100 [&_pre]:p-2 [&_pre]:text-xs [&_ul]:list-disc [&_ul]:pl-5 ${className}`}
    >
      <ReactMarkdown rehypePlugins={[rehypeSanitize]}>{raw}</ReactMarkdown>
    </div>
  );
}
